import { TYPE_OF } from '../constants/type-of'

const TRIM_LEFT_REGEX = /^\s+/

/**
 * Check if value is a string.
 *
 * @private
 * @param {*} [string] - The value to be checked
 * @returns {boolean} Whether the given value is a string or not
 */
export function isString(string) {
    return typeof string === TYPE_OF.STRING || string instanceof String
}

/**
 * Helper to convert camel-case strings to a dashed version
 * which is useful for e.g. generating t3sel classnames from
 * variables.
 *
 * @example
 * camelCaseToDash('myCustomString') === 'my-custom-string'
 * camelCaseToDash('MyCustomString') === 'my-custom-string'
 *
 * @see {@link https://gist.github.com/youssman/745578062609e8acac9f}
 * @see {@link https://github.com/lodash/lodash/blob/master/kebabCase.js}
 *
 * @param {*} [string] - CamelCased input
 * @returns {string} Converted dashed version
 */
export function camelCaseToKebabCase(string) {
    if (!isString(string)) {
        return ''
    }

    return string
        .replace(/(^[A-Z])/, ([first]) => first.toLowerCase())
        .replace(/([A-Z])/g, ([letter]) => `-${letter.toLowerCase()}`)
}

/**
 * Helper to convert all caps strings to a dashed version
 * which is useful for e.g. generating t3sel classnames from
 * constant definitions.
 *
 * @example
 * allCapsSnakeCaseToKebabCase('MY_CONSTANT') === 'my-constant'
 *
 * @param {*} [string] - All caps input
 * @returns {string} Converted dashed version
 */
export function allCapsSnakeCaseToKebabCase(string) {
    if (!isString(string)) {
        return ''
    }

    return string.toLowerCase().replace('_', '-')
}

/**
 * Check if given value is a valid string with at least 1 char.
 *
 * @example
 * isValidString() === false
 * isValidString('') === false
 * isValidString('foo') === true
 *
 * @param {*} [string] - The value to be checked
 * @returns {boolean} Whether the given value is a valid string or not
 */
export function isValidString(string) {
    return isString(string) && string.length > 0
}

/**
 * Trim whitespace from string.
 *
 * @example
 * trimString() === ''
 * trimString('') === ''
 * trimString('   foo   ') === 'foo'
 *
 * @param {*} [string] - The value to be checked
 * @returns {boolean} The trimmed string value
 */
export function trimString(string) {
    if (!isValidString(string)) {
        return ''
    }

    return string.trim()
}

/**
 * Convert a string to lowercase and just the first letter uppercase.
 *
 * @example
 * capitalizeFirstLetter() === ''
 * capitalizeFirstLetter('INPUT') === 'Input'
 * capitalizeFirstLetter('inPut') === 'Input'
 *
 * @param {*} [string] - The value to be converted
 * @returns {boolean} The converted string value
 */
export function capitalizeFirstLetter(string) {
    if (!isValidString(string)) {
        return ''
    }

    const result = string.toLowerCase()
    return result.charAt(0).toUpperCase() + result.slice(1)
}

/**
 * Get a left trimmed and lower-cased version of a string.
 * Useful for sorting strings...
 *
 * @example
 * trimLeftAndLowerCaseString() === ''
 * trimLeftAndLowerCaseString('  My-Test  ') === 'my-test  '
 *
 * @param {string} string - The value to be converted
 * @returns {string} The converted string value
 */
export function trimLeftAndLowerCaseString(string) {
    if (!isValidString(string)) {
        return ''
    }

    return string.replace(TRIM_LEFT_REGEX, '').toLowerCase()
}

/**
 * Remove html tags from given string.
 *
 * @example
 * stripHtmlTags('') === ''
 * stripHtmlTags(<strong>foo</strong>) === 'foo'
 *
 * @see {@link https://css-tricks.com/snippets/javascript/strip-html-tags-in-javascript/}
 * @param {string} string - Text to be checked
 * @returns {string} The stripped result
 */
export function stripHtmlTags(string) {
    if (!isValidString(string) || typeof document === 'undefined') {
        return ''
    }

    const tmp = document.createElement('DIV')
    tmp.innerHTML = string
    return tmp.textContent || tmp.innerText
}
